import { useState, useEffect } from 'react';
import { Control, FieldErrors, UseFormClearErrors } from 'react-hook-form';
import copy from 'copy-to-clipboard';
import IconLoader from '../../icons/loader';
import SidepanelTextRow from '../sidepanel-text-row';
import IconAttention from '../../icons/attention';
import IconCopy from '../../icons/copy-two';
import IconSelected from '../../icons/selected';
import {
  BaseellaStatus,
  CASP,
  IncomingTransactionGeneral,
  IncomingTransactionOrder,
  PaymentType,
} from '../types';
import { SelectItemProps } from '../../select/types';
import SidepanelAutocompleteRow from '../sidepanel-autocomplete-row';
import SidepanelSortCodeRow from '../sidepanel-sort-code-row';
import ButtonPseudo from '../../button/button-pseudo';
import SidepanelSelectRow from '../sidepanel-select-row';

import styles from '../transaction.module.css';

interface IncomingOrderContentProps {
  order: IncomingTransactionOrder;
  isLoading: boolean;
  countries?: SelectItemProps[];
  control?: Control;
  errors?: FieldErrors<IncomingTransactionGeneral & IncomingTransactionOrder>;
  isEditable?: boolean;
  paymentType?: PaymentType;
  onScrollToPaymentTypeField?: () => void;
  remitterCountryName?: string;
  remitterBankCountryName?: string;
  fields?: any;
  intermediaryBankCountryName?: string;
  caspList?: CASP[];
  currencyId?: string;
  clearErrors?: UseFormClearErrors<
    IncomingTransactionGeneral & IncomingTransactionOrder
  >;
}

export default function IncomingOrderContent({
  order,
  isLoading,
  countries,
  control,
  errors,
  paymentType,
  isEditable,
  remitterCountryName,
  remitterBankCountryName,
  intermediaryBankCountryName,
  onScrollToPaymentTypeField,
  fields,
  caspList,
  currencyId,
  clearErrors,
}: IncomingOrderContentProps): React.JSX.Element {
  const [accountNumber, setAccountNumber] = useState('');
  const [availableCasp, setAvailableCasp] = useState<CASP[]>([]);
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [collectorContact, setCollectorContact] = useState('');
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  let content;
  const enableEdit = Boolean(control);

  let fieldsContent;
  let remitterCountryValue = '';
  let remitterBankCountryValue = '';
  let intermediaryBankCountryValue = '';

  if (!control) {
    remitterCountryValue = remitterCountryName as string;
    remitterBankCountryValue = remitterBankCountryName as string;
    intermediaryBankCountryValue = intermediaryBankCountryName as string;
  } else if (enableEdit) {
    remitterCountryValue = order?.remitterCountry as string;
    remitterBankCountryValue = order?.remitterBankCountry as string;
    intermediaryBankCountryValue = order?.intermediaryBankCountry as string;
  }

  const onCopy = () => {
    if (order?.externalId) {
      copy(order?.externalId);

      setCopyIsSuccess(true);

      setTimeout(() => {
        setCopyIsSuccess(false);
      }, 2000);
    }
  };

  useEffect(() => {
    if (order && order.senderAccountNumber) {
      setAccountNumber(order.senderAccountNumber);
    }
  }, [order]);

  useEffect(() => {
    if (order && order.collectorContact) {
      setCollectorContact(order.collectorContact);
    }

    if (order && order.remitterFullName) {
      setBeneficiaryName(order.remitterFullName);
    }
  }, [order]);

  useEffect(() => {
    if (
      paymentType === PaymentType.CRYPTO_ASSET_PAYMENT &&
      currencyId &&
      caspList
    ) {
      const availableCasps: CASP[] = [];

      caspList.forEach((item) => {
        if (
          item.currencies?.find(
            (currency) => currency.currencyId === currencyId,
          )
        ) {
          availableCasps.push(item);
        }
      });

      setAvailableCasp(availableCasps);
    }
  }, [paymentType, caspList, currencyId]);

  if (
    paymentType === PaymentType.SEPA_SCT ||
    paymentType === PaymentType.SEPA_SCT_INST
  ) {
    fieldsContent = (
      <SidepanelTextRow
        title="Account / IBAN"
        value={enableEdit ? '' : order?.senderAccountNumber}
        name="senderAccountNumber"
        placeholder="Enter Account / IBAN"
        fieldValue={accountNumber}
        setFieldValue={setAccountNumber}
        control={control}
        enableEdit={enableEdit}
        disabled={!isEditable || Boolean(order)}
        tabIndex={!isEditable || Boolean(order) ? -1 : 0}
        error={errors?.senderAccountNumber?.message}
        copyEnabled={Boolean(order)}
        copyValue={
          enableEdit ? fields?.senderAccountNumber : order?.senderAccountNumber
        }
        clearErrors={clearErrors}
      />
    );
  } else if (
    paymentType === PaymentType.BACS ||
    paymentType === PaymentType.FASTER_PAYMENT ||
    paymentType === PaymentType.SWIFT ||
    paymentType === PaymentType.TARGET_2 ||
    paymentType === PaymentType.FEDWIRE ||
    paymentType === PaymentType.UAE_FTS ||
    paymentType === PaymentType.UAE_IPP
  ) {
    fieldsContent = (
      <SidepanelTextRow
        title={
          paymentType === PaymentType.UAE_FTS ||
          paymentType === PaymentType.UAE_IPP
            ? 'IBAN'
            : `Account Number`
        }
        value={enableEdit ? '' : order?.senderAccountNumber}
        name="senderAccountNumber"
        placeholder="Enter Account number"
        fieldValue={accountNumber}
        setFieldValue={setAccountNumber}
        control={control}
        enableEdit={enableEdit}
        disabled={!isEditable || Boolean(order)}
        tabIndex={!isEditable || Boolean(order) ? -1 : 0}
        error={errors?.senderAccountNumber?.message}
        copyEnabled={Boolean(order)}
        copyValue={
          enableEdit ? fields?.senderAccountNumber : order?.senderAccountNumber
        }
        clearErrors={clearErrors}
      />
    );
  } else if (paymentType === PaymentType.CRYPTO_ASSET_PAYMENT) {
    fieldsContent = (
      <SidepanelTextRow
        title="Wallet address"
        value={enableEdit ? '' : order?.senderAccountNumber}
        name="senderAccountNumber"
        placeholder="Enter Wallet address"
        fieldValue={accountNumber}
        setFieldValue={setAccountNumber}
        control={control}
        enableEdit={enableEdit}
        disabled={!isEditable || Boolean(order)}
        tabIndex={!isEditable || Boolean(order) ? -1 : 0}
        error={errors?.senderAccountNumber?.message}
        copyEnabled={Boolean(order)}
        copyValue={
          enableEdit ? fields?.senderAccountNumber : order?.senderAccountNumber
        }
        clearErrors={clearErrors}
      />
    );
  }

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    content = (
      <>
        <div className={styles.transactionBlockWrapper}>
          {!paymentType && enableEdit && (
            <div className={styles.attentionWrapper}>
              <IconAttention />
              <div className={styles.attentionText}>
                First select Payment type
              </div>
              <ButtonPseudo
                text="Select"
                bold
                onClick={onScrollToPaymentTypeField}
                tabIndex={-1}
              />
            </div>
          )}
          <div className={styles.transactionTitle}>Originators Info</div>
          {fieldsContent}
          <SidepanelTextRow
            title="Originators Full Name"
            value={enableEdit ? '' : order?.remitterFullName}
            name="remitterFullName"
            placeholder="Enter Originators Full Name"
            control={control}
            setFieldValue={setBeneficiaryName}
            fieldValue={beneficiaryName}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              Boolean(
                order && order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
              )
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              Boolean(
                order && order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
              )
            }
            error={errors?.remitterFullName?.message}
            copyEnabled={Boolean(order)}
            copyValue={
              enableEdit ? fields?.remitterFullName : order?.remitterFullName
            }
            clearErrors={clearErrors}
          />
          <SidepanelAutocompleteRow
            title="Country"
            value={remitterCountryValue}
            name="remitterCountry"
            placeholder="Choose Originators Country"
            control={control}
            autocompleteList={countries}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              Boolean(
                order && order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
              )
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              Boolean(
                order && order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
              )
            }
            error={errors?.remitterCountry?.message}
            copyEnabled={Boolean(order)}
            copyValue={
              enableEdit
                ? countries?.find(
                    (item) => item.value === fields?.remitterCountry,
                  )?.name
                : remitterCountryValue
            }
            withClearButton
            clearErrors={clearErrors}
          />
          {paymentType !== PaymentType.CASH_OVER_COUNTER && (
            <>
              <SidepanelTextRow
                title="City"
                value={enableEdit ? '' : order?.remitterCity || '-'}
                name="remitterCity"
                placeholder="Enter Originators City"
                control={control}
                enableEdit={enableEdit}
                tabIndex={
                  !isEditable ||
                  Boolean(
                    order &&
                    order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    order &&
                    order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
                  )
                }
                error={errors?.remitterCity?.message}
                copyEnabled={Boolean(order)}
                copyValue={
                  enableEdit ? fields?.remitterCity : order?.remitterCity
                }
                clearErrors={clearErrors}
              />
              <SidepanelTextRow
                title="Address"
                value={enableEdit ? '' : order?.remitterAddress}
                name="remitterAddress"
                placeholder="Enter Originators Address"
                control={control}
                enableEdit={enableEdit}
                tabIndex={
                  !isEditable ||
                  Boolean(
                    order &&
                    order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    order &&
                    order?.baseellaStatus !== BaseellaStatus.MISSING_PAYER,
                  )
                }
                error={errors?.remitterAddress?.message}
                copyEnabled={Boolean(order)}
                copyValue={
                  enableEdit ? fields?.remitterAddress : order?.remitterAddress
                }
                clearErrors={clearErrors}
              />
              {(paymentType === PaymentType.CRYPTO_ASSET_PAYMENT ||
                (order && order.blockchainTransactionHashId)) && (
                <SidepanelTextRow
                  title="Blockchain Hash Id"
                  value={enableEdit ? '' : order?.blockchainTransactionHashId}
                  name="blockchainTransactionHashId"
                  placeholder="Enter Blockchain Hash Id"
                  control={control}
                  enableEdit={enableEdit}
                  tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                  disabled={!isEditable || Boolean(order)}
                  error={errors?.blockchainTransactionHashId?.message}
                  copyEnabled={Boolean(order)}
                  copyValue={
                    enableEdit
                      ? fields?.blockchainTransactionHashId
                      : order?.blockchainTransactionHashId
                  }
                  clearErrors={clearErrors}
                />
              )}
            </>
          )}
          {paymentType === PaymentType.CASH_OVER_COUNTER && (
            <>
              <SidepanelTextRow
                title="Collector Contact"
                value={enableEdit ? '' : order?.collectorContact}
                name="collectorContact"
                placeholder="Enter Collectors Phone number"
                control={control}
                enableEdit={enableEdit}
                tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                disabled={!isEditable || Boolean(order)}
                setFieldValue={setCollectorContact}
                fieldValue={collectorContact}
                error={errors?.collectorContact?.message}
                copyEnabled={Boolean(order)}
                copyValue={
                  enableEdit
                    ? fields?.collectorContact
                    : order?.collectorContact
                }
                clearErrors={clearErrors}
              />
              <SidepanelTextRow
                title="PIN / Customer Password (optional)"
                value={enableEdit ? '' : order?.customPassword}
                name="customPassword"
                placeholder="Enter PIN or Customer Password"
                control={control}
                enableEdit={enableEdit}
                tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                disabled={!isEditable || Boolean(order)}
                error={errors?.customPassword?.message}
                copyEnabled={Boolean(order)}
                copyValue={
                  enableEdit ? fields?.customPassword : order?.customPassword
                }
                clearErrors={clearErrors}
              />
            </>
          )}
        </div>
        {paymentType !== PaymentType.CRYPTO_ASSET_PAYMENT &&
          paymentType !== PaymentType.CASH_OVER_COUNTER && (
            <>
              <div className={styles.transactionBlockWrapper}>
                <div className={styles.transactionTitle}>Originators Bank</div>
                {order?.externalId && (
                  <div className={styles.externalId}>
                    <div>{`External ID: ${order.externalId}`}</div>
                    <button
                      type="button"
                      onClick={onCopy}
                      className={styles.externalIdCopyButton}
                    >
                      {copyIsSuccess ? (
                        <IconSelected width={13} height={13} />
                      ) : (
                        <IconCopy width={16} height={16} />
                      )}
                    </button>
                  </div>
                )}
                {paymentType !== PaymentType.FASTER_PAYMENT &&
                  paymentType !== PaymentType.BACS &&
                  paymentType !== PaymentType.FEDWIRE && (
                    <SidepanelTextRow
                      title="BIC"
                      value={enableEdit ? '' : order?.remitterBankBIC}
                      name="remitterBankBIC"
                      placeholder="Enter Originator Bank BIC"
                      control={control}
                      enableEdit={enableEdit}
                      tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                      disabled={!isEditable || Boolean(order)}
                      error={errors?.remitterBankBIC?.message}
                      copyEnabled={Boolean(order)}
                      copyValue={
                        enableEdit
                          ? fields?.remitterBankBIC
                          : order?.remitterBankBIC
                      }
                      clearErrors={clearErrors}
                    />
                  )}

                {(paymentType === PaymentType.BACS ||
                  paymentType === PaymentType.FASTER_PAYMENT) && (
                  <SidepanelSortCodeRow
                    title="Sort Code"
                    value={order?.remitterBankSortCode}
                    name="remitterBankSortCode"
                    placeholder="Enter Sort Code"
                    control={control}
                    enableEdit={enableEdit}
                    disabled={!isEditable || Boolean(order)}
                    tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                    error={errors?.remitterBankSortCode?.message}
                    copyEnabled={Boolean(order)}
                    fieldValue={fields?.remitterBankSortCode}
                    copyValue={
                      enableEdit
                        ? fields?.remitterBankSortCode
                        : order?.remitterBankSortCode
                    }
                    clearErrors={clearErrors}
                  />
                )}
                {paymentType === PaymentType.FEDWIRE && (
                  <SidepanelTextRow
                    title="ABA"
                    value={enableEdit ? '' : order?.aba}
                    name="aba"
                    placeholder="Enter ABA"
                    control={control}
                    enableEdit={enableEdit}
                    tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                    disabled={!isEditable || Boolean(order)}
                    error={errors?.aba?.message}
                    copyEnabled={Boolean(order)}
                    copyValue={enableEdit ? fields?.aba : order?.aba}
                    clearErrors={clearErrors}
                  />
                )}

                <SidepanelTextRow
                  title="Bank Name"
                  value={enableEdit ? '' : order?.remitterBankName}
                  name="remitterBankName"
                  placeholder="Enter Originator Bank Name"
                  control={control}
                  enableEdit={enableEdit}
                  tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                  disabled={!isEditable || Boolean(order)}
                  error={errors?.remitterBankName?.message}
                  copyEnabled={Boolean(order)}
                  copyValue={
                    enableEdit
                      ? fields?.remitterBankName
                      : order?.remitterBankName
                  }
                  clearErrors={clearErrors}
                />
                <SidepanelAutocompleteRow
                  title="Country"
                  value={remitterBankCountryValue}
                  name="remitterBankCountry"
                  placeholder="Choose Originator Bank Country"
                  control={control}
                  autocompleteList={countries}
                  enableEdit={enableEdit}
                  tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                  disabled={!isEditable || Boolean(order)}
                  error={errors?.remitterBankCountry?.message}
                  copyEnabled={Boolean(order)}
                  copyValue={
                    enableEdit
                      ? countries?.find(
                          (item) => item.value === fields?.remitterBankCountry,
                        )?.name
                      : remitterBankCountryValue
                  }
                  clearErrors={clearErrors}
                />
                <SidepanelTextRow
                  title="Address"
                  value={enableEdit ? '' : order?.remitterBankAddress}
                  name="remitterBankAddress"
                  placeholder="Enter Originator Bank Address"
                  control={control}
                  enableEdit={enableEdit}
                  tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                  disabled={!isEditable || Boolean(order)}
                  error={errors?.remitterBankAddress?.message}
                  copyEnabled={Boolean(order)}
                  copyValue={
                    enableEdit
                      ? fields?.remitterBankAddress
                      : order?.remitterBankAddress
                  }
                  clearErrors={clearErrors}
                />
              </div>
              {(paymentType === PaymentType.SWIFT ||
                paymentType === PaymentType.TARGET_2) && (
                <div className={styles.transactionBlockWrapper}>
                  <div className={styles.transactionTitle}>
                    Intermediary Bank
                  </div>
                  <SidepanelTextRow
                    title="BIC"
                    value={enableEdit ? '' : order?.intermediaryBankBIC}
                    name="intermediaryBankBIC"
                    placeholder="Enter Intermediary Bank BIC"
                    control={control}
                    enableEdit={enableEdit}
                    tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                    disabled={!isEditable || Boolean(order)}
                    error={errors?.intermediaryBankBIC?.message}
                    copyEnabled={Boolean(order)}
                    copyValue={
                      enableEdit
                        ? fields?.intermediaryBankBIC
                        : order?.intermediaryBankBIC
                    }
                    clearErrors={clearErrors}
                  />
                  <SidepanelTextRow
                    title="Bank Correspondent Account"
                    value={
                      enableEdit
                        ? ''
                        : order?.intermediaryBankCorrespondentAccount
                    }
                    name="intermediaryBankCorrespondentAccount"
                    placeholder="Enter Intermediary Bank Correspondent Account"
                    control={control}
                    enableEdit={enableEdit}
                    tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                    disabled={!isEditable || Boolean(order)}
                    error={
                      errors?.intermediaryBankCorrespondentAccount?.message
                    }
                    copyEnabled={Boolean(order)}
                    copyValue={
                      enableEdit
                        ? fields?.intermediaryBankCorrespondentAccount
                        : order?.intermediaryBankCorrespondentAccount
                    }
                    clearErrors={clearErrors}
                  />
                  <SidepanelTextRow
                    title="Bank Name"
                    value={enableEdit ? '' : order?.intermediaryBankName}
                    name="intermediaryBankName"
                    placeholder="Enter Intermediary Bank Name"
                    control={control}
                    enableEdit={enableEdit}
                    tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                    disabled={!isEditable || Boolean(order)}
                    error={errors?.intermediaryBankName?.message}
                    copyEnabled={Boolean(order)}
                    copyValue={
                      enableEdit
                        ? fields?.intermediaryBankName
                        : order?.intermediaryBankName
                    }
                    clearErrors={clearErrors}
                  />
                  <SidepanelAutocompleteRow
                    title="Country"
                    value={enableEdit ? '' : order?.intermediaryBankCountry}
                    name="intermediaryBankCountry"
                    placeholder="Choose Intermediary Bank Country"
                    control={control}
                    autocompleteList={countries}
                    enableEdit={enableEdit}
                    tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                    disabled={!isEditable || Boolean(order)}
                    error={errors?.intermediaryBankCountry?.message}
                    copyEnabled={Boolean(order)}
                    copyValue={
                      enableEdit
                        ? countries?.find(
                            (item) =>
                              item.value === fields?.intermediaryBankCountry,
                          )?.name
                        : intermediaryBankCountryValue
                    }
                    clearErrors={clearErrors}
                  />
                  <SidepanelTextRow
                    title="Address"
                    value={enableEdit ? '' : order?.intermediaryBankAddress}
                    name="intermediaryBankAddress"
                    placeholder="Enter Intermediary Bank Address"
                    control={control}
                    enableEdit={enableEdit}
                    tabIndex={!isEditable || Boolean(order) ? -1 : 0}
                    disabled={!isEditable || Boolean(order)}
                    error={errors?.intermediaryBankAddress?.message}
                    copyEnabled={Boolean(order)}
                    copyValue={
                      enableEdit
                        ? fields?.intermediaryBankAddress
                        : order?.intermediaryBankAddress
                    }
                    clearErrors={clearErrors}
                  />
                </div>
              )}
            </>
          )}
        {paymentType === PaymentType.CRYPTO_ASSET_PAYMENT && (
          <div className={styles.transactionBlockWrapper}>
            <div className={styles.transactionTitle}>Beneficiary CASP</div>
            <SidepanelSelectRow
              title="CASP"
              value={enableEdit ? '' : order?.caspId}
              name="caspId"
              placeholder="Choose CASP"
              control={control}
              selectList={availableCasp?.map((item) => ({
                name: item.name,
                value: item.id,
              }))}
              enableEdit={enableEdit}
              disabled={!isEditable || Boolean(order)}
              tabIndex={!isEditable || Boolean(order) ? -1 : 0}
              error={errors?.caspId?.message}
              copyEnabled={Boolean(order)}
              copyValue={
                enableEdit
                  ? availableCasp?.find((item) => item.id === fields?.caspId)
                      ?.name
                  : availableCasp?.find((item) => item.id === order?.caspId)
                      ?.name
              }
              withClearButton
              clearErrors={clearErrors}
            />
          </div>
        )}
      </>
    );
  }

  return content;
}
